import { call, fork, put, select, take, takeLatest } from 'typed-redux-saga';

import { SELF } from '../../../../../shared/constants';
import { getRoomAgentConfig } from '../../../../../shared/features/config/selectors';
import { goloomSdk } from '../../../../../shared/features/goloom-sdk';
import { RECORDER_RESOLUTIONS } from '../../../../../shared/features/room-agent/constants';
import { RoomAgentHaltError } from '../../../../../shared/features/room-agent/errors';
import { RecorderVideo } from '../../../../../shared/features/room-agent/recorder/RecorderVideo';
import { mapConfErrorToHaltDescriptor } from '../../../../../shared/features/room-agent/sagas';
import { recordSaga } from '../../../../../shared/features/room-agent/sagas/record';
import { SignalingAPI } from '../../../../../shared/features/room-agent/SignalingAPI';
import { ChannelHolder } from '../../../../../shared/helpers/channelHolder';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { recordHelpers } from '../../../sagas/root';
import {
    disconnectConferenceAction,
    disconnectedFromConferenceDoneAction,
    updateParticipant,
} from '../../conference/slice';
import { debugSaga } from './sagas/debug';
import { dtmfSaga } from './sagas/dtmf';
import { statsSaga } from './sagas/stats';

const logger = createLogger('goloom:room-agent');

const setLocalTrack = function* (track: MediaStreamTrack) {
    const localTrack = yield* call([goloomSdk, goloomSdk.replaceLocalTrack], track, true);
    if (localTrack) {
        yield put(
            updateParticipant({
                id: SELF,
                [localTrack.kind]: {
                    id: localTrack.id,
                    muted: false,
                },
            })
        );
    }
};

export function* initRoomAgentSaga() {
    logger.debug('init');

    const config = yield* select(getRoomAgentConfig);
    const debugMode = config?.debug;

    const channelHolder = new ChannelHolder();
    const signaling = new SignalingAPI(debugMode);

    try {
        logger.debug('ready sending');
        yield* call([signaling, signaling.ready]);

        yield* takeLatest(signaling.terminateEventChannel, function* (event) {
            logger.debug('handling terminate signal');

            try {
                yield* put(disconnectConferenceAction());
                yield* take(disconnectedFromConferenceDoneAction);
            } catch (error) {
                logger.error(error, {
                    method: 'signaling.terminateEventChannel',
                });
            } finally {
                event.resolve();
            }
        });

        const recorderVideo = new RecorderVideo({
            resolution: RECORDER_RESOLUTIONS[1080],
            h264ProfileLevelId: config?.h264ProfileLevelId,
        });

        yield* call(setLocalTrack, recorderVideo.getTrack());

        yield* channelHolder.takeEvery(goloomSdk.onInit, function* () {
            logger.debug('set local track');
            yield* call(setLocalTrack, recorderVideo.getTrack());
        });

        yield* fork(dtmfSaga, { signaling });
        yield* fork(debugSaga, { signaling });
        yield* fork(recordSaga, { recorderVideo, helpers: recordHelpers, logger });
        yield* fork(statsSaga, { signaling, channelHolder });

        const disconnectDoneResult = yield* take(disconnectedFromConferenceDoneAction);

        if (disconnectDoneResult.payload?.error) {
            const descriptor = mapConfErrorToHaltDescriptor(disconnectDoneResult.payload.error);

            throw new RoomAgentHaltError(descriptor.reason, descriptor.description, {
                method: 'rootRoomAgentSaga',
            });
        }
    } catch (error) {
        const normalizedError = RoomAgentHaltError.from(error, {
            method: 'rootRoomAgentSaga',
        });

        logger.error(normalizedError);

        yield* call([signaling, signaling.halt], {
            reason: normalizedError.reason,
            description: normalizedError.description,
        });
        yield* put(disconnectConferenceAction());
    } finally {
        channelHolder.close();
        logger.debug('close room-agent');
    }
}
